import React from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { CONTACT_INFO, BRAND_INFO } from '../data/mock';
import { Shield, Mail, Phone } from 'lucide-react';
import usePageTitle from '../hooks/usePageTitle';

const PrivacyPage = () => {
  usePageTitle('Privacy Policy', 'Read our privacy policy to learn how Stitch Your Dash protects and uses your personal information.');
  return (
    <div className="min-h-screen bg-warmWhite-100">
      <Header />
      <CartDrawer />

      <main className="pt-[80px] md:pt-[120px] lg:pt-[136px] pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-rose-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-rose-500" />
            </div>
            <h1 className="font-display text-3xl lg:text-4xl text-warmBrown-900 mb-4">Privacy Policy</h1>
            <p className="text-warmBrown-500">Last updated: January 2025</p>
          </div>

          <div className="bg-white rounded-3xl p-6 lg:p-10 shadow-card border border-cream-200 space-y-8">
            <section>
              <h2 className="font-display text-xl text-charcoal-900 mb-4">Introduction</h2>
              <p className="text-charcoal-600 leading-relaxed">
                {BRAND_INFO.name} ("we," "our," or "us") respects your privacy and is committed to protecting your personal data.
                This privacy policy explains how we collect, use, and safeguard your information when you visit our website or make a purchase.
              </p>
            </section>

            <section>
              <h2 className="font-display text-xl text-warmBrown-900 mb-4">Information We Collect</h2>
              <ul className="text-warmBrown-600 leading-relaxed space-y-2">
                <li>• <strong>Personal Information:</strong> Name, email address, shipping address, phone number</li>
                <li>• <strong>Payment Information:</strong> Credit card details (processed securely through our payment provider)</li>
                <li>• <strong>Order Information:</strong> Products purchased, order history</li>
                <li>• <strong>Technical Information:</strong> IP address, browser type, device information</li>
              </ul>
            </section>

            <section>
              <h2 className="font-display text-xl text-warmBrown-900 mb-4">How We Use Your Information</h2>
              <ul className="text-warmBrown-600 leading-relaxed space-y-2">
                <li>• Process and fulfill your orders</li>
                <li>• Communicate with you about your purchases</li>
                <li>• Send promotional emails (with your consent)</li>
                <li>• Improve our website and services</li>
                <li>• Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="font-display text-xl text-warmBrown-900 mb-4">No Sharing With Third Parties</h2>
              <p className="text-warmBrown-600 leading-relaxed mb-4">
                We will never share, sell, rent, or disclose your personal information to any third party for their marketing or commercial use.
              </p>
              <p className="text-warmBrown-600 leading-relaxed mb-4">
                Your data is used only by {BRAND_INFO.name} and trusted service providers who help us operate our website and serve you (e.g., payment processors and order fulfillment partners), and only to the extent necessary to provide our services.
              </p>
              <p className="text-warmBrown-600 leading-relaxed mb-2">We do not:</p>
              <ul className="text-warmBrown-600 leading-relaxed space-y-2">
                <li>• Sell customer lists to advertisers</li>
                <li>• Share personal data with social media platforms for advertising</li>
                <li>• Disclose personal information to unrelated third parties without your consent</li>
              </ul>
            </section>

            <section>
              <h2 className="font-display text-xl text-warmBrown-900 mb-4">When Information is Shared (Limited Cases Only)</h2>
              <p className="text-warmBrown-600 leading-relaxed mb-2">In limited situations, we may share information with:</p>
              <ul className="text-warmBrown-600 leading-relaxed space-y-2 mb-4">
                <li>• Service providers acting on our behalf (e.g., payment processors, order fulfillment services)</li>
                <li>• Legal authorities if required by law</li>
              </ul>
              <p className="text-warmBrown-600 leading-relaxed">
                These parties are obligated to protect your data and are prohibited from using it for other purposes.
              </p>
            </section>

            <section>
              <h2 className="font-display text-xl text-warmBrown-900 mb-4">Data Security</h2>
              <p className="text-warmBrown-600 leading-relaxed">
                We implement appropriate security measures to protect your personal information. All payment transactions
                are encrypted using SSL technology. We do not store your complete credit card information on our servers.
              </p>
            </section>

            <section>
              <h2 className="font-display text-xl text-warmBrown-900 mb-4">Your Rights</h2>
              <p className="text-warmBrown-600 leading-relaxed">
                You have the right to access, correct, or delete your personal information. You can also opt-out of
                marketing communications at any time. To exercise these rights, please contact us using the information below.
              </p>
            </section>

            <section className="bg-rose-50 rounded-2xl p-6 border border-rose-100">
              <h2 className="font-display text-lg text-warmBrown-900 mb-4">Contact Us</h2>
              <p className="text-warmBrown-600 mb-4">
                If you have any questions about this Privacy Policy, please contact us:
              </p>
              <div className="space-y-2">
                <a
                  href={`mailto:${CONTACT_INFO.email}`}
                  className="flex items-center gap-2 text-rose-600 hover:text-rose-700 transition-colors"
                >
                  <Mail className="w-5 h-5" />
                  {CONTACT_INFO.email}
                </a>
                <a
                  href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
                  className="flex items-center gap-2 text-rose-600 hover:text-rose-700 transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  {CONTACT_INFO.phone}
                </a>
                <p className="text-warmBrown-600">
                  {CONTACT_INFO.address.street}, {CONTACT_INFO.address.city}, {CONTACT_INFO.address.state} {CONTACT_INFO.address.zip}
                </p>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPage;
